import React, { useState } from 'react';
import { Alert, Button, Checkbox, Form, Image, Input, InputNumber, Modal, Select, Space, Table, Upload, message } from 'antd';
import { PaperClipOutlined, PlusOutlined } from '@ant-design/icons';
import TimezoneAwareTime from '../../components/TimezoneAwareTime';
import {
  createNonStdDestroyRecord, setNonStdNoDestroy, updateNonStdDestroyRecord, uploadNonStdAttachment, voidNonStdDestroyRecord,
} from '../../services/nonstandardOps';
import { NonStdAttachment, NonStdDestroyInfo, NonStdDestroyRow, NonStdDestroyTotals, NonStdDestroyUnitOption } from '../../types/nonstandardOps';
import { getAttachmentUrl } from '../../utils/attachmentUrl';
import { useAlignedImagePreview } from '../../utils/imagePreviewControls';
import { describeUploadError, ensureUploadSize } from '../../utils/uploadLimits';
import MediaPreviewModal, { IMAGE_EXT, MediaPreviewTarget, previewKindOf } from './MediaPreviewModal';

const errorText = (err: any, fallback: string) => {
  const data = err?.response?.data;
  return data?.detail || (data && typeof data === 'object' ? Object.values(data).flat().join('；') : '') || err?.message || fallback;
};

/** 合计的文字：2 条，共 5 件，净重 350.5 g */
export const destroyTotalsText = (totals?: NonStdDestroyTotals | null) => (
  `${totals?.count ?? 0} 条，共 ${totals?.qty ?? 0} 件，净重 ${Number(totals?.net_weight_g ?? 0)} g`
);

const unitOptionLabel = (option: NonStdDestroyUnitOption) => {
  const contents = (option.content_items || []).map(it => `${it.name} × ${it.qty}`).join('，');
  return contents ? `${option.code}（${contents}）` : option.code;
};

interface TableProps {
  records: NonStdDestroyRow[];
  totals?: NonStdDestroyTotals | null;
  onEdit?: (record: NonStdDestroyRow) => void;
  onVoid?: (record: NonStdDestroyRow) => void;
}

/** 销毁记录表（只读时不传操作）：执行页、终审页、阶段快照共用 */
export const DestroyRecordsTable: React.FC<TableProps> = ({ records, totals, onEdit, onVoid }) => {
  const [previewTarget, setPreviewTarget] = useState<MediaPreviewTarget | null>(null);
  // 照片预览：把左右翻页和关闭按钮挪到照片旁边（和执行记录的照片一样）
  const onImagePreviewVisibleChange = useAlignedImagePreview();

  return (
  <div>
    <Table
      className="nonstd-destroy-table"
      size="small"
      rowKey="id"
      pagination={false}
      dataSource={records}
      scroll={{ x: 860 }}
      columns={[
        { title: '库存单元', dataIndex: 'unit_code', width: 170, render: (v: string) => v || '-' },
        { title: '产品说明', dataIndex: 'product_text', width: 170, render: (v: string) => v || '-' },
        { title: '件数', dataIndex: 'qty', width: 70 },
        { title: '净重(g)', dataIndex: 'net_weight_g', width: 90, render: (v: number | string) => Number(v) },
        { title: '备注', dataIndex: 'remark', render: (v: string) => v || '-' },
        {
          title: '附件',
          width: 170,
          render: (_: any, row: NonStdDestroyRow) => ((row.attachments || []).length === 0 ? '-' : (
            <Space wrap size={6} className="nonstd-destroy-attachments">
              {/* 照片：缩略图，点开弹窗预览（同一条记录的照片可以左右翻页），和执行记录一样 */}
              <Image.PreviewGroup preview={{ onVisibleChange: onImagePreviewVisibleChange }}>
                {row.attachments.filter(att => IMAGE_EXT.test(att.url)).map(att => (
                  <Image key={att.url} src={getAttachmentUrl({ url: att.url })} width={48} height={48} style={{ objectFit: 'cover', borderRadius: 4 }} />
                ))}
              </Image.PreviewGroup>
              {row.attachments.filter(att => !IMAGE_EXT.test(att.url)).map(att => {
                const url = getAttachmentUrl({ url: att.url });
                const name = att.name || att.url.split('/').pop() || '附件';
                // 视频、PDF、表格（Excel/CSV）在弹窗里预览；Word 等其他格式不能预览，仍在新标签页打开
                if (previewKindOf(name) || previewKindOf(att.url)) {
                  return <a key={att.url} className="nonstd-destroy-attachment-preview" onClick={() => setPreviewTarget({ url, name })}>{name}</a>;
                }
                return <a key={att.url} href={url} target="_blank" rel="noopener noreferrer">{name}</a>;
              })}
            </Space>
          )),
        },
        {
          title: '记录人',
          width: 130,
          render: (_: any, row: NonStdDestroyRow) => (
            <div style={{ fontSize: 12 }}>
              <div>{row.created_by_name || '-'}{row.edit_count ? <span style={{ color: '#8c8c8c' }}>（改过 {row.edit_count} 次）</span> : null}</div>
              <div style={{ color: '#8c8c8c' }}><TimezoneAwareTime time={row.created_at} format="YYYY-MM-DD HH:mm" showTimezone={false} /></div>
            </div>
          ),
        },
        ...(onEdit || onVoid ? [{
          title: '操作',
          width: 90,
          render: (_: any, row: NonStdDestroyRow) => (
            <Space size={8}>
              {onEdit ? <a className="nonstd-destroy-edit" onClick={() => onEdit(row)}>修改</a> : null}
              {onVoid ? <a className="nonstd-destroy-void" style={{ color: '#cf1322' }} onClick={() => onVoid(row)}>作废</a> : null}
            </Space>
          ),
        }] : []),
      ]}
    />
    <div className="nonstd-destroy-totals" style={{ marginTop: 8, fontWeight: 600 }}>合计：{destroyTotalsText(totals)}</div>
    <MediaPreviewModal target={previewTarget} onClose={() => setPreviewTarget(null)} />
  </div>
  );
};

interface ModalProps {
  instructionId: number;
  /** null = 新增 */
  record: NonStdDestroyRow | null;
  unitOptions: NonStdDestroyUnitOption[];
  onClose: () => void;
  onSaved: () => void;
}

/** 新增/修改销毁记录的弹窗：打开时才挂载，表单实例和初始值每次都是新的 */
const DestroyRecordModal: React.FC<ModalProps> = ({ instructionId, record, unitOptions, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const [attachments, setAttachments] = useState<NonStdAttachment[]>(record?.attachments || []);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  // 改记录时，原来选的库存单元可能已经不在可选范围里：放进选项，不然下拉显示成一个数字
  const options = [...unitOptions];
  if (record?.inventory_unit_id && !options.some(o => o.id === record.inventory_unit_id)) {
    options.push({ id: record.inventory_unit_id, code: record.unit_code || `#${record.inventory_unit_id}`, content_items: [] });
  }

  const submit = async () => {
    let values: any;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const payload = {
      inventory_unit_id: values.inventory_unit_id ?? null,
      product_text: String(values.product_text || '').trim(),
      qty: values.qty,
      net_weight_g: values.net_weight_g,
      remark: String(values.remark || '').trim(),
      attachments,
    };
    setSaving(true);
    try {
      if (record) await updateNonStdDestroyRecord(record.id, payload);
      else await createNonStdDestroyRecord(instructionId, payload);
      message.success('销毁记录已保存');
      onSaved();
    } catch (err: any) {
      message.error(errorText(err, '保存失败'));
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (file: File) => {
    if (!ensureUploadSize(file)) return false;
    setUploading(true);
    try {
      const uploaded = await uploadNonStdAttachment(file);
      setAttachments(prev => [...prev, uploaded]);
    } catch (err: any) {
      message.error(`“${file.name}”上传失败：${describeUploadError(err)}`);
    } finally {
      setUploading(false);
    }
    return false;
  };

  return (
    <Modal
      title={record ? '修改销毁记录' : '添加销毁记录'}
      open
      okText="保存"
      cancelText="取消"
      confirmLoading={saving}
      onOk={() => { void submit(); }}
      onCancel={onClose}
      width={560}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={record ? {
          inventory_unit_id: record.inventory_unit_id ?? undefined,
          product_text: record.product_text,
          qty: record.qty,
          net_weight_g: Number(record.net_weight_g),
          remark: record.remark,
        } : undefined}
      >
        <Form.Item name="inventory_unit_id" label="库存单元（选填）" extra="说不清属于哪个库存单元时可以不选，在产品说明里描述。">
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="从本指令锁定的库存单元里选"
            options={options.map(o => ({ value: o.id, label: unitOptionLabel(o) }))}
          />
        </Form.Item>
        <Form.Item name="product_text" label="产品说明（选填）">
          <Input maxLength={300} placeholder="例如：破损的蓝色T恤" />
        </Form.Item>
        <Space size={16} align="start">
          <Form.Item name="qty" label="销毁件数" rules={[{ required: true, message: '请填写销毁件数' }]}>
            <InputNumber className="nonstd-destroy-qty" min={1} precision={0} style={{ width: 160 }} addonAfter="件" />
          </Form.Item>
          <Form.Item name="net_weight_g" label="净重（这一批合计）" rules={[{ required: true, message: '请填写净重' }]}>
            <InputNumber className="nonstd-destroy-weight" min={0.01} precision={2} style={{ width: 200 }} addonAfter="g" />
          </Form.Item>
        </Space>
        <Form.Item name="remark" label="备注（选填）">
          <Input.TextArea rows={2} maxLength={500} placeholder="例如销毁原因、销毁方式" />
        </Form.Item>
        <Space wrap align="center">
          <Upload multiple showUploadList={false} beforeUpload={handleUpload as any} disabled={uploading || attachments.length >= 20}>
            <Button icon={<PaperClipOutlined />} loading={uploading}>上传照片/附件（选填）</Button>
          </Upload>
          {attachments.map((att, index) => (
            <span key={att.url} style={{ fontSize: 12 }}>
              {att.name || att.url.split('/').pop()}
              <a style={{ marginLeft: 4 }} onClick={() => setAttachments(prev => prev.filter((_, i) => i !== index))}>移除</a>
            </span>
          ))}
        </Space>
      </Form>
    </Modal>
  );
};

interface PanelProps {
  instructionId: number;
  info: NonStdDestroyInfo;
  /** "确认完成"阶段：没有销毁记录时要勾选"本指令没有发生销毁"，否则不能提交本阶段 */
  askDeclaration?: boolean;
  onChanged: () => void;
}

/**
 * 销毁记录：执行中直接销毁时，记录销毁了多少件、净重多少（克）。
 * 只是现场记录，不动库存（库存由管理员在终审"库存结算"里处理）；只给仓库员工和管理员看，客户看不到。
 * 不能编辑时（指令结束、"确认完成"已提交、只读页面）只显示记录。
 */
const DestroyRecordsPanel: React.FC<PanelProps> = ({ instructionId, info, askDeclaration, onChanged }) => {
  const [editing, setEditing] = useState<NonStdDestroyRow | 'new' | null>(null);
  const [declaring, setDeclaring] = useState(false);

  const canEdit = !!info.can_edit;
  const records = info.records || [];

  const handleVoid = (record: NonStdDestroyRow) => {
    let reason = '';
    Modal.confirm({
      title: '作废这条销毁记录？',
      content: (
        <div>
          <div style={{ marginBottom: 8 }}>作废后不再显示和合计，但会保留在系统里以便追溯。</div>
          <Input placeholder="作废原因（选填）" onChange={e => { reason = e.target.value; }} />
        </div>
      ),
      okText: '作废',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        try {
          await voidNonStdDestroyRecord(record.id, reason);
          message.success('已作废');
          onChanged();
        } catch (err: any) {
          message.error(errorText(err, '作废失败'));
          throw err;
        }
      },
    });
  };

  const declare = async (declared: boolean) => {
    setDeclaring(true);
    try {
      await setNonStdNoDestroy(instructionId, declared);
      onChanged();
    } catch (err: any) {
      message.error(errorText(err, '保存失败'));
    } finally {
      setDeclaring(false);
    }
  };

  return (
    <div className="nonstd-destroy-panel">
      {canEdit ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 10 }}
          message="执行中有直接销毁的产品，请在这里记录件数和净重。只给仓库内部和管理员看，客户看不到；这里只是记录，不会扣减库存，库存由管理员在终审时处理。"
        />
      ) : null}
      {askDeclaration && canEdit && !info.answered ? (
        <Alert
          className="nonstd-destroy-unanswered"
          type="warning"
          showIcon
          style={{ marginBottom: 10 }}
          message="请确认销毁情况：有直接销毁就添加销毁记录，没有就勾选下面的“本指令没有发生销毁”，否则不能提交本阶段。"
        />
      ) : null}
      {canEdit ? (
        <Button icon={<PlusOutlined />} className="nonstd-destroy-add" style={{ marginBottom: 10 }} onClick={() => setEditing('new')}>添加销毁记录</Button>
      ) : null}
      {records.length > 0 ? (
        <DestroyRecordsTable records={records} totals={info.totals} onEdit={canEdit ? setEditing : undefined} onVoid={canEdit ? handleVoid : undefined} />
      ) : null}
      {records.length === 0 && !canEdit && !info.no_destroy_declared ? <span style={{ color: '#8c8c8c' }}>没有销毁记录</span> : null}
      {records.length === 0 && askDeclaration && canEdit ? (
        <div>
          <Checkbox
            className="nonstd-destroy-declare"
            checked={!!info.no_destroy_declared}
            disabled={declaring}
            onChange={e => { void declare(e.target.checked); }}
          >
            本指令没有发生销毁
          </Checkbox>
        </div>
      ) : null}
      {info.no_destroy_declared ? (
        <div className="nonstd-destroy-declared" style={{ marginTop: 6, fontSize: 12, color: '#8c8c8c' }}>
          已声明本指令没有发生销毁：{info.no_destroy_by_name || '-'}
          {info.no_destroy_at ? <>，<TimezoneAwareTime time={info.no_destroy_at} format="YYYY-MM-DD HH:mm" showTimezone={false} /></> : null}
        </div>
      ) : null}
      {editing ? (
        <DestroyRecordModal
          instructionId={instructionId}
          record={editing === 'new' ? null : editing}
          unitOptions={info.unit_options || []}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onChanged(); }}
        />
      ) : null}
    </div>
  );
};

export default DestroyRecordsPanel;
